import dayjs from "dayjs";
import type { Axis, SeriesLineOptions } from "highcharts";

import type { SimplifiedSeries } from "@similarweb/pro-logic-components";
import { dayAndShortMonth, shortMonthAndShortYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";

import type { WebSource } from "app/components/filters-bar/websource-filter/WebSourceFilter.types";
import { abbrNumberFilter } from "app/filters/ngFilters";
import { GranularitySwitcherEnum } from "app/pages/industry-analysis/category-share/CategoryShareGraph/CategoryShareGraphTypes";
import { GRANULARITY_SWITCHER_OPTIONS } from "app/common/keyword-research/keywords-overview/trendOverTimeConsts";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { CHART_COLORS } from "app/constants/ChartColors";

import { EDataType } from "../../../advertisers/overview/components/types";
import { PlacementKeys } from "../types";

export const getEmptyGraphPropsWithThirdMetric = (
    spendTotal: number,
    impressionsTotal: number,
    thirdMetricTotal: number,
    thirdMetric: EDataType,
    isPreview: boolean,
): SimplifiedSeries<SeriesLineOptions> => {
    const baseItems = [
        {
            name: "ad.intelligence.overview.performance.over.time.widget.spend",
            id: EDataType.SPEND,
            total: spendTotal,
            color: CHART_COLORS.mobileWeb[0],
            visible: true,
            yAxis: null,
            data: [],
        },
        {
            name: "ad.intelligence.overview.performance.over.time.widget.impressions",
            id: EDataType.IMPRESSIONS,
            total: impressionsTotal,
            color: CHART_COLORS.mobileWeb[1],
            visible: true,
            yAxis: null,
            data: [],
        },
        {
            name: `ad.intelligence.overview.performance.over.time.widget.${thirdMetric}`,
            id: thirdMetric,
            total: thirdMetricTotal,
            color: colorsPalettes.carbon[250],
            visible: true,
            yAxis: null,
            data: [],
        },
    ];
    if (isPreview) {
        return [baseItems[2], baseItems[0], baseItems[1]];
    }

    return baseItems;
};

export const granularityNameToValue: Record<GranularityNames, GranularitySwitcherEnum> = {
    [GranularityNames.DAILY]: GranularitySwitcherEnum.DAILY,
    [GranularityNames.WEEKLY]: GranularitySwitcherEnum.WEEKLY,
    [GranularityNames.MONTHLY]: GranularitySwitcherEnum.MONTHLY,
};

export const granularityValueToName: Record<GranularitySwitcherEnum, GranularityNames> = {
    [GranularitySwitcherEnum.DAILY]: GranularityNames.DAILY,
    [GranularitySwitcherEnum.WEEKLY]: GranularityNames.WEEKLY,
    [GranularitySwitcherEnum.MONTHLY]: GranularityNames.MONTHLY,
};

const shouldUseMonthlyOnly = (selectedWebSource: WebSource, hasAdIntelMetrics: boolean, isWindow: boolean): boolean => {
    return selectedWebSource !== devicesTypes.DESKTOP && !hasAdIntelMetrics && !isWindow;
};

export const getCustomGranularitySwitcherOptions = (
    selectedWebSource: WebSource,
    hasAdIntelMetrics: boolean,
    isGraphLoading: boolean,
    isWindow: boolean,
    hideDailyGranularity = false,
) => {
    return GRANULARITY_SWITCHER_OPTIONS.filter(
        (item) => !(hideDailyGranularity && item.value === GranularityNames.DAILY),
    ).map((item) => {
        let disabled = isGraphLoading;

        if (item.value === GranularityNames.MONTHLY && isWindow) {
            disabled = true;
        }

        if (
            item.value !== GranularityNames.MONTHLY &&
            shouldUseMonthlyOnly(selectedWebSource, hasAdIntelMetrics, isWindow)
        ) {
            disabled = true;
        }

        return {
            ...item,
            disabled,
        };
    });
};

export const adPlacementConfig = [
    {
        color: CHART_COLORS.mobileDesktopColors[0],
        translationKey: "ad.intelligence.overview.ad.placement.widget.above.the.fold",
        id: PlacementKeys.ABOVE_THE_FOLD,
    },
    {
        color: CHART_COLORS.mobileDesktopColors[1],
        translationKey: "ad.intelligence.overview.ad.placement.widget.below.the.fold",
        id: PlacementKeys.BELOW_THE_FOLD,
    },
    {
        color: colorsPalettes.carbon[50],
        translationKey: "ad.intelligence.overview.ad.placement.widget.far.below.the.fold",
        id: PlacementKeys.FAR_BELOW_THE_FOLD,
    },
];

export const fillMissingPlacementKeys = (
    placement: Partial<Record<PlacementKeys, number>>,
): Record<PlacementKeys, number> => {
    return {
        [PlacementKeys.ABOVE_THE_FOLD]: placement[PlacementKeys.ABOVE_THE_FOLD] ?? 0,
        [PlacementKeys.BELOW_THE_FOLD]: placement[PlacementKeys.BELOW_THE_FOLD] ?? 0,
        [PlacementKeys.FAR_BELOW_THE_FOLD]: placement[PlacementKeys.FAR_BELOW_THE_FOLD] ?? 0,
    };
};

export const normalizePercentages = (values: number[]): number[] => {
    if (!values.length || values.every((v) => v === 0)) {
        return values;
    }

    const percentages = values.map((v) => Math.round(v * 100));
    const total = percentages.reduce((sum, p) => sum + p, 0);

    if (total === 100) {
        return percentages.map((p) => p / 100);
    }

    const difference = 100 - total;
    const maxIndex = percentages.indexOf(Math.max(...percentages));
    percentages[maxIndex] += difference;

    return percentages.map((p) => p / 100);
};

export const MAX_DATA_POINTS_FOR_ROTATION = 50;

export const yAxisFormatter = ({ value }: { value: number }) => `$${abbrNumberFilter()(value)}`;

export const xAxisFormatter = ({ value }: { value: number }, isMonthly: boolean) =>
    dayjs.utc(value).format(isMonthly ? shortMonthAndShortYear : dayAndShortMonth);

export const legendToFormatterMap: Record<EDataType, (value: number) => string> = {
    [EDataType.SPEND]: (value) => yAxisFormatter({ value }),
    [EDataType.IMPRESSIONS]: abbrNumberFilter(),
    [EDataType.VISITS]: abbrNumberFilter(),
    [EDataType.PAGE_VIEWS]: abbrNumberFilter(),
};

const getSinglePointTickPositioner = (axisIndex = 0) =>
    function (this: Axis) {
        return [0, this.chart.yAxis[axisIndex].max, this.chart.yAxis[axisIndex].max * 2];
    };

export const getTickPositionerConfig = (isSinglePoint: boolean, axisIndex = 0) =>
    isSinglePoint
        ? { tickPositioner: getSinglePointTickPositioner(axisIndex) }
        : { tickAmount: 4, tickPositioner: undefined };

const MAX_TICKS = 20;

export const getXAxisTickPositioner = function (this: Axis): number[] | undefined {
    const seriesToUse =
        this.chart.series.find((series) => series.visible && series.data?.length > 0) || this.chart.series[0];

    const userOptions = seriesToUse?.userOptions as { data?: Array<[number, number]> } | undefined;
    const seriesData = userOptions?.data || [];
    const data = seriesData.map((point) => point[0]).filter((x) => x != null);

    if (!data || data.length === 0) {
        return undefined;
    }

    // If data length is <= MAX_TICKS, show all data points
    if (data.length <= MAX_TICKS) {
        return [...data];
    }

    // Find the largest number of ticks (≤ MAX_TICKS) that divides evenly
    // For evenly spaced ticks, (data.length - 1) should be divisible by (numTicks - 1)
    // This ensures integer step sizes and no skipping
    const dataRange = data.length - 1;

    // Try from MAX_TICKS down to find evenly divisible step
    for (let numTicks = MAX_TICKS; numTicks >= 2; numTicks--) {
        const intervals = numTicks - 1;

        // Check if dataRange is divisible by intervals (gives us integer step)
        if (dataRange % intervals === 0) {
            const step = dataRange / intervals;
            const positions: number[] = [];

            // Generate evenly spaced positions
            for (let i = 0; i < numTicks; i++) {
                positions.push(data[i * step]);
            }

            return positions;
        }
    }

    // Fallback: if no perfect divisor found, use MAX_TICKS with rounding
    // This handles edge cases where data.length doesn't divide evenly
    const step = dataRange / (MAX_TICKS - 1);
    const positions = [data[0]];
    const usedIndices = new Set([0]);

    for (let i = 1; i < MAX_TICKS - 1; i++) {
        const index = Math.round(step * i);
        if (!usedIndices.has(index) && index < data.length) {
            positions.push(data[index]);
            usedIndices.add(index);
        }
    }

    const lastIndex = data.length - 1;
    if (!usedIndices.has(lastIndex)) {
        positions.push(data[lastIndex]);
    }

    return positions;
};
